import { CounterpartyAdminAgents, CounterpartyComposition } from "./counterparty-admin-agents.jsx";
import { readySolutionSettingsHref } from "./ready-solution-admin.mjs";
import { recommendedLimitLabel } from "./counterparty-limits.mjs";
import {
  prepareCounterpartyDraft,
  sourceStateLabel,
  sourceReadyForResearch,
  enablePublicSourceSearch,
  preparationProblem,
  preparationSourceOutcomes,
} from "./counterparty-settings.mjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { idempotencyKey } from "./helpers.mjs";
import { TaskLaunchNumber } from "./task-launch-number.jsx";

const root = "/v1/platform/counterparty-v2";
const categories = {
  registration: "Реквизиты",
  ownership_management: "Руководство и участники",
  address_activities: "Адрес и деятельность",
  finance: "Финансы",
  staff_taxes: "Численность и налоги",
  licenses: "Лицензии",
  legal_events: "Юридические события",
  relations: "Связи",
};
const labels = {
  complete: "Сбор завершён",
  partial: "Часть сведений собрана",
  no_data: "Содержательные сведения не получены",
  collected: "Сведения получены",
  not_found: "В просмотренных материалах не найдено",
  unavailable: "Недоступно",
  not_checked: "Не проверено",
  retrieved: "Материал сохранён",
  queued: "В очереди",
  running: "Выполняется",
  completed: "Материалы подготовлены",
  failed: "Исполнение остановлено",
  cancelled: "Отменено",
  matched: "По первичному источнику",
  provisional: "Предварительно, по вторичному источнику",
};
const purposeNames = {
  general: "Общая проверка",
  supplier: "Наш поставщик",
  customer: "Наш покупатель",
};
const date = (value) =>
  value ? new Date(value).toLocaleString("ru-RU") : "Не указана";
const csv = (value) =>
  value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
function download(value, type, name) {
  const url = URL.createObjectURL(
    new Blob(
      [typeof value === "string" ? value : JSON.stringify(value, null, 2)],
      { type },
    ),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function PreparationSources({ diagnostic, journal }) {
  const rows = preparationSourceOutcomes(diagnostic, journal);
  if (!rows.length) return null;
  return (
    <section>
      <h3>Получение страниц при подготовке</h3>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Источник</th>
              <th>Результат</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.title}
                  {row.url && (
                    <p>
                      <a href={row.url} target="_blank" rel="noreferrer">
                        Открыть страницу
                      </a>
                    </p>
                  )}
                </td>
                <td>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function counterpartyMaterialMarkdown(dossier) {
  const isReview = dossier.researcherRole === "factchecker_4";
  const label = {researcher_1_1:"1.1",researcher_2:"2",analyst_3:"3",factchecker_4:"4"}[dossier.researcherRole] || "1";
  const heading = isReview ? "Замечания Агента 4" : `Материалы Агента ${label}`;
  const note = isReview ? (dossier.verification?.agent4Completed ? "Проверка явных ошибок завершена. Остальные сведения справки не считаются подтверждёнными." : "Проверка не завершена. Это не означает отсутствие явных ошибок.") : "Черновые материалы. Фактчекинг не выполнен.";
  return `# ${heading} · ИНН ${dossier.input.inn}\n\n${note}\n\n${dossier.markdown || (isReview ? "Ответ проверки не получен." : "Ответ исследования не получен.")}\n\n## Источники\n\n${(dossier.sources || []).map((source) => `- ${source.title || source.url}: ${source.url}`).join("\n")}\n\n${(dossier.warnings || []).join("\n\n")}`;
}

export function CounterpartyDossier({ dossier, journal }) {
  const [researcher, setResearcher] = useState(dossier.analysis?.markdown?.trim() ? "3" : "1");
  if (dossier.parallel || dossier.independent || dossier.analysis || dossier.factCheck) {
    const stages = { "1": { ...dossier, parallel: undefined, independent: undefined, analysis: undefined, factCheck: undefined }, ...(dossier.parallel ? {"1.1":dossier.parallel} : {}), ...(dossier.independent ? {"2":dossier.independent} : {}), ...(dossier.analysis ? {"3":dossier.analysis} : {}), ...(dossier.factCheck ? {"4":dossier.factCheck} : {}) };
    const selected = stages[researcher] ? researcher : "1";
    const current = stages[selected];
    const name = `counterparty-${dossier.input.inn}-agent-${selected}${selected === "4" ? "-remarks" : ""}`;
    return <section className="counterparty-dossier-pair">
      <h3>{dossier.independent || dossier.analysis || dossier.factCheck ? "Материалы этапов решения" : "Материалы параллельных исследователей"}</h3>
      {dossier.factCheck && selected !== "4" && <button type="button" className="button ghost small" onClick={() => download(counterpartyMaterialMarkdown(dossier.factCheck), "text/markdown", `counterparty-${dossier.input.inn}-agent-4-remarks.md`)}>Скачать замечания Агента 4 (MD)</button>}
      <nav className="section-nav" aria-label="Материалы этапов">
        {["1", "1.1", "2", "3", "4"].filter((id) => stages[id]).map((id) => <button type="button" className={`button ${selected === id ? "primary" : "ghost"}`} key={id} aria-pressed={selected === id} onClick={() => setResearcher(id)}>Агент {id}{id === "3" ? " · Сводка" : id === "4" ? " · Замечания" : ""}</button>)}
      </nav>
      <div className="counterparty-material-downloads">
        <button type="button" className="button ghost small" onClick={() => download(counterpartyMaterialMarkdown(current), "text/markdown", `${name}.md`)}>Скачать {selected === "4" ? "замечания " : ""}MD Агента {selected}</button>
        <button type="button" className="button ghost small" onClick={() => download(current, "application/json", `${name}.json`)}>Скачать JSON Агента {selected}</button>
      </div>
      <CounterpartyDossier dossier={current} journal={journal}/>
    </section>;
  }
  if (dossier.artifactKind === "counterparty-researcher-1-web-draft") {
    return (
      <div className="counterparty-dossier">
        <h3>
          {dossier.company?.name || `Материалы по ИНН ${dossier.input.inn}`}
        </h3>
        <p>
          ИНН {dossier.input.inn}
          {dossier.company?.ogrn ? ` · ОГРН ${dossier.company.ogrn}` : ""}
        </p>
        <p className="counterparty-warning">
          {dossier.researcherRole === "factchecker_4"
            ? dossier.verification?.agent4Completed ? "Замечания Агента 4. Проверка явных ошибок завершена. Остальные сведения справки не считаются подтверждёнными." : "Замечания Агента 4. Проверка не завершена; это не означает отсутствие явных ошибок."
            : `${dossier.researcherRole === "analyst_3" ? "Сводный черновик" : "Черновое исследование"} Агента ${{researcher_1_1:"1.1",researcher_2:"2",analyst_3:"3"}[dossier.researcherRole] || "1"}. Фактчекинг не выполнен.`}
        </p>
        <p className="muted">
          {purposeNames[dossier.input.purpose]} · {date(dossier.createdAt)}
          {dossier.model ? ` · ${dossier.model}` : ""}
        </p>
        {dossier.budgetDiagnostic && <ResearchBudget diagnostic={dossier.budgetDiagnostic} />}
        {dossier.markdown ? (
          <ResearchText text={dossier.markdown} />
        ) : (
          <p>
            {dossier.researcherRole === "factchecker_4" ? "Ответ проверки не получен. Это не означает отсутствие явных ошибок." : "Ответ исследования не получен. Это не означает отсутствие сведений о компании."}
          </p>
        )}
        <h4>{dossier.researcherRole === "analyst_3" ? "Источники материалов" : dossier.researcherRole === "factchecker_4" ? "Источники проверки" : "Источники веб-поиска"}</h4>
        <p className="muted">
          {dossier.researcherRole === "analyst_3" ? "Ссылки из материалов исследователей." : "Ссылки, возвращённые инструментом поиска."} Отдельные страницы не архивировались; сведения требуют проверки.
        </p>
        <ul>
          {dossier.sources.map((source) => (
            <li key={source.url}>
              <a href={source.url} target="_blank" rel="noreferrer">
                {source.title || source.url}
              </a>
            </li>
          ))}
        </ul>
        {dossier.stopReason && (
          <p className="counterparty-warning">
            Исследование завершено с ограничением:{" "}
            {{
              budget_limit: "недостаточно свободного бюджета для резерва следующего запроса",
              organization_budget_limit: "запрос не прошёл проверку бюджета организации",
              beta_budget_limit: "запрос не прошёл проверку бюджета бесплатной беты",
              time_limit: "истекло время исследования",
              analysis_unavailable: "сводка Агента 3 не получена; проверка не выполнялась",
              web_research_failed: dossier.researcherRole === "analyst_3" ? "не удалось подготовить сводку" : "не удалось получить ответ веб-исследования",
              composition_failed:
                "не удалось выполнить заключительное оформление",
            }[dossier.stopReason] || "часть действий не завершена"}
            . {dossier.markdown ? "Собранные материалы сохранены." : "Ответ этого этапа не получен."}
          </p>
        )}
        {dossier.warnings.map((warning, index) => (
          <p className="muted" key={index}>
            {warning}
          </p>
        ))}
      </div>
    );
  }
  if (dossier.artifactKind === "identity-resolution") {
    const problem = preparationProblem(dossier, journal);
    return (
      <div className="counterparty-dossier">
        <h3>{problem.title}</h3>
        <p>{problem.description} Агент 1 не запускался.</p>
        {dossier.code === "identity_unavailable" && (
          <PreparationSources diagnostic={dossier} journal={journal} />
        )}
        {dossier.candidates?.map((c, i) => (
          <p key={i}>
            {c.name} · ИНН {c.inn} · ОГРН {c.ogrn}
          </p>
        ))}
        {dossier.citations?.map((c) => (
          <blockquote key={c.id}>{c.quote}</blockquote>
        ))}
      </div>
    );
  }
  const sources = new Map(dossier.sources.map((s) => [s.id, s]));
  const displaySources = dossier.sources.filter(
    (s) =>
      s.id !== `s-${s.sourceRegistryId}` ||
      !dossier.sources.some(
        (other) =>
          other.id !== s.id && other.sourceRegistryId === s.sourceRegistryId,
      ),
  );
  const citations = new Map(dossier.citations.map((c) => [c.id, c]));
  return (
    <div className="counterparty-dossier">
      <h3>{dossier.company.name}</h3>
      <p>
        ИНН {dossier.company.inn} · ОГРН {dossier.company.ogrn}
      </p>
      <p className="counterparty-warning">
        Исследовательские материалы. Итоговый фактчекинг не выполнен.
      </p>
      <p>
        {purposeNames[dossier.input.purpose]} · {date(dossier.createdAt)} ·{" "}
        {labels[dossier.company.identityStatus]}
      </p>
      <h4>{labels[dossier.status]}</h4>
      <p>{dossier.summary}</p>
      <p>
        Обязательные проверки: {dossier.completion.completedRequiredCheckCount}{" "}
        из {dossier.completion.requiredCheckCount}. Исходные содержательные
        факты: {dossier.completion.usefulFactCount}.
      </p>
      {Object.entries(categories).map(([category, title]) => (
        <section key={category}>
          <h4>{title}</h4>
          {dossier.facts.filter(
            (f) =>
              (f.category === "staff" ? "staff_taxes" : f.category) ===
              category,
          ).length === 0 && (
            <p className="muted">
              Сведения не получены. Это не означает отсутствие соответствующих
              обстоятельств.
            </p>
          )}
          {dossier.facts
            .filter(
              (f) =>
                (f.category === "staff" ? "staff_taxes" : f.category) ===
                category,
            )
            .map((fact) => (
              <article className="counterparty-fact" key={fact.id}>
                {fact.claimType === "source_opinion" && (
                  <p className="muted">Оценка источника</p>
                )}
                <p>{fact.statement}</p>
                {fact.period && <p className="muted">Период: {fact.period}</p>}
                {fact.collectionStatus === "needs_recheck" && (
                  <p className="counterparty-warning">
                    Требует перепроверки: {fact.uncertaintyReason}.{" "}
                    {fact.recheckAction}
                  </p>
                )}
                {fact.calculation && (
                  <details>
                    <summary>
                      Расчёт SI Harness: {fact.calculation.displayValue}
                    </summary>
                    <p>
                      {fact.calculation.expression} ·{" "}
                      {fact.calculation.formulaId}@
                      {fact.calculation.formulaVersion}
                    </p>
                    {fact.calculation.inputs.map((a) => (
                      <p key={a.name}>
                        {a.name}: {a.value} {a.unit}, {a.period} · факт{" "}
                        {a.factId}
                      </p>
                    ))}
                    <p>
                      Точное значение: {fact.calculation.resultExact.numerator}/
                      {fact.calculation.resultExact.denominator}. Округление:{" "}
                      {fact.calculation.rounding.decimalPlaces} знаков,{" "}
                      {fact.calculation.rounding.mode}, только при отображении.
                    </p>
                    {fact.calculation.limitations.map((l, i) => (
                      <p key={i}>{l}</p>
                    ))}
                  </details>
                )}
                {fact.citationIds.map((id) => {
                  const c = citations.get(id);
                  const s = sources.get(c?.sourceId);
                  return (
                    c && (
                      <details key={id}>
                        <summary>
                          {s?.title || c.sourceId} · {date(s?.retrievedAt)}
                        </summary>
                        <blockquote>{c.quote}</blockquote>
                        <p className="muted">
                          {c.location} · Дата документа:{" "}
                          {s?.documentDate || "не установлена"}
                        </p>
                        {s?.url?.startsWith("https://") && (
                          <a href={s.url} target="_blank" rel="noreferrer">
                            Открыть источник
                          </a>
                        )}
                      </details>
                    )
                  );
                })}
              </article>
            ))}
        </section>
      ))}
      <h4>Расхождения и вопросы</h4>
      {dossier.conflicts.map((c) => (
        <p key={c.id}>
          {c.description}. {c.recheckAction}
        </p>
      ))}
      {dossier.gaps.map((g) => (
        <p key={g.id}>
          {g.description}. {g.reason}. {g.nextQuestion}
        </p>
      ))}
      <h4>Охват исследования</h4>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Блок</th>
              <th>Состояние</th>
              <th>Ограничение</th>
            </tr>
          </thead>
          <tbody>
            {dossier.coverage.map((c) => (
              <tr key={c.category}>
                <td>{categories[c.category]}</td>
                <td>{labels[c.status] || c.status}</td>
                <td>{c.limitation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h4>Источники</h4>
      {displaySources.map((s) => (
        <p key={s.id}>
          {s.title}: {labels[s.accessStatus] || s.accessStatus}.{" "}
          {s.accessNote || ""}{" "}
          {s.url?.startsWith("https://") && (
            <a href={s.url} target="_blank" rel="noreferrer">
              Материал
            </a>
          )}
        </p>
      ))}
      {dossier.warnings.map((w, i) => (
        <p className="muted" key={i}>
          {w}
        </p>
      ))}
    </div>
  );
}

function ResearchBudget({ diagnostic: b }) {
  const scope = { task: "запуска", organization: "организации", beta: "бесплатной беты" }[b.scope] || "запуска";
  const amount = (value) => `${Number(value).toLocaleString("ru-RU", { maximumFractionDigits: 9 })} ${b.currency || "USD"}`;
  return <section className="counterparty-warning" aria-label="Причина ограничения бюджета">
    <h4>Запрос остановлен проверкой бюджета {scope}</h4>
    <p>{b.model ? `${b.model} · ` : ""}{{ search: "Поиск", composition: "Оформление материалов", analysis: "Подготовка сводки" }[b.operation] || "Запрос модели"}.</p>
    {b.scope === "task" && <>
      <p>{b.reason === "active_reservations" ? "Часть бюджета занята выполняющимися или ещё не рассчитанными запросами. " : ""}Перед отправкой резервируется верхняя стоимость запроса. Резерв учитывает предел контекста, выходных токенов и поисковых вызовов; фактический ответ может стоить значительно меньше.</p>
      <div className="table-wrap"><table>
        <caption>Суммы на момент отказа</caption>
        <tbody>{[
          ["Лимит запуска", b.limit],
          ["Фактические расходы", b.actualAmount],
          ["Учтено для ограничения", b.chargedAmount],
          ["Занятый резерв", b.reservedAmount],
          ["Требуемый резерв запроса", b.requestedAmount],
          ["Доступно для запроса", b.availableAmount],
        ].filter(([,value]) => value !== undefined && value !== "").map(([label,value]) => <tr key={label}><th scope="row">{label}</th><td>{amount(value)}</td></tr>)}</tbody>
      </table></div>
      <p>Поисковых вызовов в запросе: до {b.searchCalls || 0}. Выходных токенов: до {b.outputTokens || 0}.{b.activeCalls ? ` Незавершённых расчётов: ${b.activeCalls}.` : ""} Учтённая сумма может превышать фактические расходы из-за консервативного тарифа или ещё не подтверждённого расхода.</p>
    </>}
    {b.scope !== "task" && <p>Это отдельное ограничение бюджета. Свободная сумма лимита запуска не отменяет проверку бюджета {scope}.</p>}
  </section>;
}

// Model output stays text: no HTML interpretation or active links from an
// unverified narrative. Clickable sources come from the filtered tool list.
function ResearchText({ text }) {
  return (
    <div className="counterparty-research-text">
      {text.split(/\n\s*\n/).map((block, index) => {
        if (/^#{1,6}\s/.test(block) && !block.includes("\n"))
          return <h4 key={index}>{block.replace(/^#{1,6}\s+/, "")}</h4>;
        return (
          <p
            key={index}
            style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {block}
          </p>
        );
      })}
    </div>
  );
}

export function CounterpartyPilot({
  api,
  onCatalogChanged,
  mode = "settings",
  adminView = "solutions",
  selectedAgent = "",
  onAdminStateChange,
  refreshRevision = 0,
  readiness,
  models = { items: [], loading: false, error: null },
  onReloadModels,
}) {
  const [settings, setSettings] = useState(null),
    [draft, setDraft] = useState(null),
    [items, setItems] = useState([]);
  const [view, setView] = useState(mode === "solution" ? "pilot" : "settings"),
    [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [inn, setInn] = useState(""),
    [purpose, setPurpose] = useState("general"),
    [replacement, setReplacement] = useState(null);
  const [selected, setSelected] = useState(null),
    [dossier, setDossier] = useState(null),
    [technical, setTechnical] = useState(null),
    [materialView, setMaterialView] = useState("dossier");
  const intent = useRef(null);
  useEffect(() => {
    if (adminView === "solutions" && mode !== "solution") setView("settings");
  }, [adminView, mode]);
  const load = useCallback(async () => {
    const [s, h] = await Promise.all([
      api.request(`${root}/settings`),
      api.request(`${root}/tasks`),
    ]);
    setSettings(s);
    setDraft(prepareCounterpartyDraft(s.draft, s));
    setItems(h.items);
    return h.items;
  }, [api]);
  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load, refreshRevision]);
  useEffect(() => {
    onAdminStateChange?.({ settings, error: settings ? null : error, unsaved: Boolean(settings && draft && JSON.stringify(draft) !== JSON.stringify(settings.draft)) });
  }, [onAdminStateChange, settings, draft, error]);
  const action = async (key, callback) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      await callback();
    } catch (e) {
      setError(e.message || "Действие не выполнено.");
    } finally {
      setBusy("");
    }
  };
  const change = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const saveDraft = async () => {
    const s = await api.request(`${root}/settings`, {
      method: "PUT",
      body: { revision: settings.revision, draft },
    });
    setSettings(s);
    setDraft(s.draft);
    setNotice("Черновик сохранён. Изменения применяются после публикации решения.");
  };
  const limit = (key, value) =>
    setDraft((d) => ({
      ...d,
      limits: {
        ...d.limits,
        [key]: value === "" ? null : key === "amount" ? value : Number(value),
      },
    }));
  const source = (id, key, value) =>
    setDraft((d) => ({
      ...d,
      sources: d.sources.map((s) =>
        s.sourceId === id ? { ...s, [key]: value } : s,
      ),
    }));
  const addSource = () =>
    setDraft((d) => ({
      ...d,
      sources: [
        ...d.sources,
        {
          sourceId: `source-${Date.now().toString(36)}`,
          name: "Новый источник",
          domains: [],
          paths: ["/"],
          type: "secondary",
          categories: [],
          originPublisher: "",
          priority: 0,
          method: "public_page",
          state: "unavailable",
          enabled: false,
          urlTemplate: "",
          adapterVersion: "public_document@1",
          freshnessDays: 0,
        },
      ],
    }));
  const open = async (task) => {
    setSelected(task);
    setDossier(null);
    setTechnical(null);
    setMaterialView("dossier");
    try {
      const material = await api.request(
        `${root}/tasks/${task.taskId}/dossier`,
      );
      setDossier(material);
      if (material.artifactKind === "identity-resolution") {
        try {
          setTechnical(
            await api.request(`${root}/tasks/${task.taskId}/journal`),
          );
        } catch {
          setNotice(
            "Подробный журнал пока недоступен. Сохранённые материалы доступны ниже.",
          );
        }
      }
    } catch (e) {
      if (e.status !== 404) throw e;
      setNotice(
        "Досье пока не упаковано. Для остановленного задания можно восстановить сохранённые материалы.",
      );
    }
  };
  if (!settings || !draft)
    return (
      <article className="counterparty-pilot">
        <h3>Справка о контрагенте</h3>
        <p>{error || "Загружаем пилот…"}</p>
      </article>
    );
  const disabled = Boolean(busy);
  const unsaved = JSON.stringify(draft) !== JSON.stringify(settings.draft);
  const publicSearchSources = settings.recommendedSources
    ? enablePublicSourceSearch(draft.sources, settings.recommendedSources)
    : null;
  const publicSearchPending = publicSearchSources?.some((source, index) =>
    !draft.sources[index] ||
    source.enabled !== draft.sources[index].enabled ||
    source.state !== draft.sources[index].state,
  );
  const missingPublicSources = publicSearchSources?.slice(draft.sources.length) || [];
  const enabledPublicSources = draft.sources.filter((source) =>
    source.method === "public_page" && sourceReadyForResearch(source),
  ).length;
  if (adminView === "agents") return <article className="counterparty-pilot">
    <CounterpartyAdminAgents draft={draft} selectedAgent={selectedAgent} recommendedPrompt={settings.recommendedPrompt} recommendedAgentPrompts={settings.recommendedAgentPrompts} models={models} onReloadModels={onReloadModels} disabled={disabled} onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}/>
    {error && <p role="alert" className="inline-problem">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    <p className="muted">Опубликованная версия решения: {settings.published?.version || "нет"}. Агенты и решение используют один черновик.</p>
    {unsaved && <p className="counterparty-warning" role="status">Есть несохранённые изменения черновика.</p>}
    {selectedAgent && <div className="counterparty-actions"><button type="button" className="button" disabled={disabled || !unsaved} onClick={() => action("save", saveDraft)}>{busy === "save" ? "Сохраняем…" : "Сохранить черновик"}</button><a className="button ghost" href={readySolutionSettingsHref()}>К публикации решения</a></div>}
  </article>;
  return (
    <article className="counterparty-pilot">
      <header className="section-heading">
        <div>
          <h3>Справка о контрагенте</h3>
          <p className="muted">
            Общее готовое решение для допущенных организаций. Здесь настраиваются состав, источники, общие ограничения и публикация.
          </p>
          <p className="muted">
            Агенты исследуют компанию через веб-поиск и сохраняют черновые досье. Модели и инструкции редактируются во вкладке «Агенты».
          </p>
        </div>
        {mode !== "solution" && (
          <label>
            <input
              type="checkbox"
              role="switch"
              checked={settings.enabled}
              disabled={disabled || !settings.published}
              onChange={(e) =>
                action("toggle", async () => {
                  const s = await api.request(`${root}/enabled`, {
                    method: "PUT",
                    body: {
                      revision: settings.revision,
                      enabled: e.target.checked,
                    },
                  });
                  setSettings(s);
                  await onCatalogChanged?.();
                })
              }
            />{" "}
            {settings.enabled ? "Решение включено" : "Решение отключено"}
          </label>
        )}
      </header>
      <nav className="section-nav" aria-label="Решение: настройки и запуск">
        {mode === "solution" ? (
          <a className="button ghost" href="#/settings/ready-solutions">
            Настройки решения
          </a>
        ) : (
          <>
            <button
              type="button"
              className="button ghost"
              onClick={() => setView("settings")}
            >
              Настройки
            </button>
            <button
              type="button"
              className="button ghost"
              onClick={() => setView("pilot")}
            >
              Запуск и материалы
            </button>
          </>
        )}
      </nav>
      {error && (
        <p role="alert" className="inline-problem">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {view === "settings" ? (
        <div>
          <p>
            Опубликованная версия: {settings.published?.version || "нет"}.
            Изменения черновика применяются после публикации.
          </p>
          {unsaved && <p className="counterparty-warning" role="status">Есть несохранённые изменения черновика.</p>}
          <CounterpartyComposition
            draft={draft}
            published={settings.published}
            recommendedAgentPrompts={settings.recommendedAgentPrompts}
            onChange={(patch) =>
              setDraft((current) => ({ ...current, ...patch }))
            }
            disabled={disabled}
          />
          <fieldset disabled={disabled}>
            <legend>Обязательные ограничения исполнения</legend>
            <div className="counterparty-fields">
              {[
                ["amount", "Жёсткий лимит, USD", "0.000001"],
                ["seconds", "Время исследования, секунд", "1"],
                ["searches", "Максимум запросов поиска", "1"],
                ["documents", "Максимум сохраняемых источников", "1"],
                ["retries", "Максимум платных повторов (0–3)", "1"],
                [
                  "documentBytes",
                  "Максимальный размер загруженного документа, байт",
                  "1",
                ],
              ].map(([key, name, step]) => (
                <label key={key}>
                  {name}
                  <input
                    type="number"
                    step={step}
                    min={key === "retries" ? "0" : step}
                    value={draft.limits[key] ?? ""}
                    onChange={(e) => limit(key, e.target.value)}
                  />
                  {settings.recommendedLimits && (
                    <small>
                      Рекомендуется:{" "}
                      {recommendedLimitLabel(
                        key,
                        settings.recommendedLimits[key],
                      )}
                    </small>
                  )}
                </label>
              ))}
            </div>
            {settings.recommendedLimits && (
              <button
                type="button"
                className="button ghost small"
                onClick={() =>
                  setDraft((current) => ({
                    ...current,
                    limits: {
                      ...current.limits,
                      ...settings.recommendedLimits,
                    },
                  }))
                }
              >
                Применить рекомендации
              </button>
            )}
            <p className="muted">
              Начальные рекомендации можно изменить перед сохранением и
              публикацией. Денежный лимит — предел расходов, а не ожидаемая
              стоимость справки. Платные обращения выполняются с резервированием
              расходов; неизвестный расход сохраняет резерв. Автоматические
              платные повторы отключены. Число действий веб-поиска дополнительно
              ограничивается доступным бюджетом. Лимит источников относится к
              сохраняемым ссылкам, размер документа — к загруженному тексту.
              {(draft.parallelResearcher || draft.independentResearcher || draft.analyst || draft.factChecker) && " Время и денежный лимит общие для всех этапов; пределы поиска и сохраняемых источников применяются к каждому исследователю."}
            </p>
          </fieldset>
          <fieldset disabled={disabled}>
            <legend>Реестр источников</legend>
            <p className="muted">
              Агент сам ищет страницы компании по ИНН в разрешённых источниках,
              открывает найденные материалы и сохраняет черновое досье и ссылки.
              Доступность страниц проверяется во время исследования. Для
              открытых сайтов отдельное подключение и проверочный URL не нужны.
              Платные и закрытые источники требуют отдельного доступа.
            </p>
            {publicSearchSources && <>
              <button
                type="button"
                className="button ghost small"
                disabled={disabled || !publicSearchPending}
                onClick={() => setDraft((current) => ({
                  ...current,
                  sources: enablePublicSourceSearch(current.sources, settings.recommendedSources),
                }))}
              >
                {publicSearchPending
                  ? missingPublicSources.length
                    ? "Добавить сайты и разрешить поиск"
                    : "Разрешить поиск по открытым источникам"
                  : enabledPublicSources
                    ? "Поиск по открытым источникам уже разрешён"
                    : "Нет открытых источников для включения"}
              </button>
              <p className="muted" role="status">
                Источников с разрешённым поиском: {enabledPublicSources}.
                {missingPublicSources.length > 0 && ` Будут добавлены сайты: ${missingPublicSources.map((source) => source.name).join(", ")}.`}
                {publicSearchPending
                  ? " Кнопка разрешает поиск в открытых источниках из стандартного списка."
                  : !enabledPublicSources
                    ? " Добавьте открытый источник или загрузите документ."
                    : " Доступность страниц проверяется при запуске исследования."}
                {" "}Изменения нужно сохранить и опубликовать. Эта кнопка не запускает исследование.
              </p>
            </>}
            {!draft.sources.some(sourceReadyForResearch) && (
              <p className="inline-problem" role="status">
                Для публикации разрешите поиск хотя бы по одному открытому
                источнику или загрузите документ.
              </p>
            )}
            {draft.sources.map((s) => (
              <details className="counterparty-source" key={s.sourceId}>
                <summary>
                  {s.name} · {s.enabled ? "включён" : "выключен"} ·{" "}
                  {sourceStateLabel(s.state)}
                </summary>
                <label>
                  <input
                    type="checkbox"
                    checked={s.enabled}
                    onChange={(e) =>
                      source(s.sourceId, "enabled", e.target.checked)
                    }
                  />{" "}
                  Использовать в плане исследования
                </label>
                {s.method === "public_page" && s.state === "searchable" && (
                  <p className="muted">
                    Сайты для поиска: {s.domains.map((domain, index) => (
                      <React.Fragment key={domain}>
                        {index > 0 && ", "}
                        <a href={`https://${domain}/`} target="_blank" rel="noreferrer">{domain}</a>
                      </React.Fragment>
                    ))}. Подключение не требуется; агент проверяет доступность найденных страниц во время исследования.
                  </p>
                )}
                <div className="counterparty-fields">
                  <label>
                    Идентификатор источника
                    <input
                      value={s.sourceId}
                      onChange={(e) =>
                        source(s.sourceId, "sourceId", e.target.value)
                      }
                    />
                  </label>
                  <label>
                    Название
                    <input
                      value={s.name}
                      onChange={(e) =>
                        source(s.sourceId, "name", e.target.value)
                      }
                    />
                  </label>
                  <label>
                    Первичный издатель
                    <input
                      value={s.originPublisher}
                      onChange={(e) =>
                        source(s.sourceId, "originPublisher", e.target.value)
                      }
                    />
                  </label>
                  <label>
                    Тип
                    <select
                      value={s.type}
                      onChange={(e) =>
                        source(s.sourceId, "type", e.target.value)
                      }
                    >
                      <option value="primary">Первичный</option>
                      <option value="secondary">Вторичный</option>
                    </select>
                  </label>
                  <label>
                    Состояние
                    <select
                      value={s.state}
                      onChange={(e) =>
                        source(s.sourceId, "state", e.target.value)
                      }
                    >
                      {[
                        "ready",
                        ...(settings.recommendedSources ? ["searchable"] : []),
                        "requires_access",
                        "manual_only",
                        "unavailable",
                        "disabled",
                      ].map((v) => (
                        <option key={v} value={v}>
                          {sourceStateLabel(v)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Получение
                    <select
                      value={s.method}
                      onChange={(e) =>
                        source(s.sourceId, "method", e.target.value)
                      }
                    >
                      <option value="public_page">Публичная страница</option>
                      <option value="uploaded_document">
                        Загруженный текст
                      </option>
                    </select>
                  </label>
                  <label>
                    Приоритет
                    <input
                      type="number"
                      value={s.priority}
                      onChange={(e) =>
                        source(s.sourceId, "priority", Number(e.target.value))
                      }
                    />
                  </label>
                  <label>
                    Допустимая давность, дней
                    <input
                      type="number"
                      min="1"
                      value={s.freshnessDays || ""}
                      onChange={(e) =>
                        source(
                          s.sourceId,
                          "freshnessDays",
                          Number(e.target.value),
                        )
                      }
                    />
                  </label>
                  <label>
                    Точные домены, через запятую
                    <input
                      value={s.domains.join(", ")}
                      onChange={(e) =>
                        source(s.sourceId, "domains", csv(e.target.value))
                      }
                    />
                  </label>
                  <label>
                    Разрешённые пути, через запятую
                    <input
                      value={s.paths.join(", ")}
                      onChange={(e) =>
                        source(s.sourceId, "paths", csv(e.target.value))
                      }
                    />
                  </label>
                  {s.method === "public_page" && s.state !== "searchable" && (
                    <>
                      <label>
                        Шаблон URL (необязательно)
                        <input
                          value={s.urlTemplate}
                          onChange={(e) =>
                            source(s.sourceId, "urlTemplate", e.target.value)
                          }
                          placeholder="https://…/{inn}"
                        />
                      </label>
                      <label>
                        URL документа для пробного получения (необязательно)
                        <input
                          value={s.probeUrl || ""}
                          onChange={(e) =>
                            source(s.sourceId, "probeUrl", e.target.value)
                          }
                          placeholder="Доступный документ на разрешённом домене"
                        />
                      </label>
                    </>
                  )}
                </div>
                <p>Блоки исследования</p>
                <div className="counterparty-categories">
                  {Object.entries(categories).map(([id, name]) => (
                    <label key={id}>
                      <input
                        type="checkbox"
                        checked={s.categories.includes(id)}
                        onChange={(e) =>
                          source(
                            s.sourceId,
                            "categories",
                            e.target.checked
                              ? [...s.categories, id]
                              : s.categories.filter((v) => v !== id),
                          )
                        }
                      />{" "}
                      {name}
                    </label>
                  ))}
                </div>
                {s.method === "uploaded_document" && (
                  <label>
                    Текстовый документ (.txt), до 1 МБ
                    <input
                      type="file"
                      accept=".txt,text/plain"
                      onChange={(e) =>
                        action("upload", async () => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 1048576)
                            throw new Error("Документ превышает 1 МБ.");
                          const text = await f.text();
                          setDraft((d) => ({
                            ...d,
                            sources: d.sources.map((v) =>
                              v.sourceId === s.sourceId
                                ? {
                                    ...v,
                                    uploadedText: text,
                                    uploadedFileName: f.name,
                                    state: "manual_only",
                                  }
                                : v,
                            ),
                          }));
                        })
                      }
                    />
                    {s.uploadedFileName && (
                      <span>Подключён: {s.uploadedFileName}</span>
                    )}
                  </label>
                )}
                {s.method === "public_page" && s.state !== "searchable" && (
                  <>
                    <p className="muted">
                      Пробное получение скачивает документ по указанному URL и проверяет извлечение текста.
                      Для поиска в интернете выберите состояние «Поиск в интернете»; проверочный URL не нужен.
                    </p>
                    <button
                      type="button"
                      className="button ghost"
                      disabled={disabled || !s.probeUrl}
                      onClick={() =>
                        action("probe", async () => {
                          const result = await api.request(`${root}/source-probe`, {
                            method: "POST",
                            body: { source: s, url: s.probeUrl },
                          });
                          if (!result.ready) throw new Error(result.reason);
                          source(s.sourceId, "state", "ready");
                          setNotice(`${s.name}: документ получен, текст извлечён.`);
                        })
                      }
                    >
                      Проверить получение документа
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="button ghost"
                  disabled={disabled}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      sources: d.sources.filter(
                        (v) => v.sourceId !== s.sourceId,
                      ),
                    }))
                  }
                >
                  Удалить из черновика
                </button>
              </details>
            ))}
            <button
              type="button"
              className="button ghost"
              disabled={disabled}
              onClick={addSource}
            >
              Добавить источник
            </button>
          </fieldset>
          <div className="counterparty-actions">
            <button
              type="button"
              className="button"
              disabled={disabled || !unsaved}
              onClick={() => action("save", saveDraft)}
            >
              Сохранить черновик
            </button>
            <button
              type="button"
              className="button ghost"
              disabled={
                disabled ||
                !settings.revision ||
                JSON.stringify(draft) !== JSON.stringify(settings.draft)
              }
              onClick={() =>
                action("publish", async () => {
                  const s = await api.request(`${root}/publish`, {
                    method: "POST",
                    body: { revision: settings.revision, enabled: false },
                  });
                  setSettings(s);
                  setNotice(
                    "Конфигурация опубликована. Включите решение для запусков.",
                  );
                  await onCatalogChanged?.();
                })
              }
            >
              Опубликовать сохранённый черновик
            </button>
            <button
              type="button"
              className="button ghost"
              disabled={disabled}
              onClick={() => action("load", load)}
            >
              Обновить
            </button>
          </div>
          <p className="muted">
            Черновик можно сохранить до завершения настройки. Для запуска
            сохраните заполненные настройки, опубликуйте черновик, включите
            решение. Пользователи запускают его в разделе «Готовые решения».
          </p>
          <details>
            <summary>
              История опубликованных версий ({settings.history.length})
            </summary>
            {settings.history.map((v) => (
              <p key={v.version}>
                {v.version} · реестр {v.registryVersion} · {date(v.publishedAt)}
              </p>
            ))}
          </details>
        </div>
      ) : (
        <div>
          <p>
            Опубликованная версия: {settings.published?.version || "нет"}.
            {settings.published && (
              <> Лимит расходов: {settings.published.limits.amount} USD.</>
            )}
          </p>
          {readiness?.ready === false && (
            <p className="inline-problem" role="status">
              {readiness.message}
            </p>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              action("launch", async () => {
                const identity = JSON.stringify({
                  inn: inn.trim(),
                  purpose,
                  replaces: replacement?.taskId,
                });
                if (!intent.current || intent.current.identity !== identity)
                  intent.current = { identity, key: idempotencyKey() };
                const task = await api.request(
                  replacement
                    ? `${root}/tasks/${replacement.taskId}/replace`
                    : `${root}/tasks`,
                  {
                    method: "POST",
                    headers: { "Idempotency-Key": intent.current.key },
                    body: { input: { inn: inn.trim(), purpose } },
                  },
                );
                intent.current = null;
                setReplacement(null);
                await load();
                await open(task);
              });
            }}
          >
            <h4>
              {replacement
                ? "Новое задание взамен остановленного"
                : "Пилотный запуск"}
            </h4>
            <div className="counterparty-fields">
              <label>
                ИНН юридического лица
                <input
                  required
                  pattern="[0-9]{10}"
                  inputMode="numeric"
                  value={inn}
                  disabled={disabled}
                  onChange={(e) => setInn(e.target.value)}
                />
              </label>
              <label>
                Цель проверки
                <select
                  value={purpose}
                  disabled={disabled}
                  onChange={(e) => setPurpose(e.target.value)}
                >
                  {Object.entries(purposeNames).map(([v, name]) => (
                    <option value={v} key={v}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <button
              type="submit"
              className="button"
              disabled={
                disabled ||
                !settings.enabled ||
                !settings.published ||
                readiness?.ready === false
              }
            >
              {busy === "launch" ? "Принимаем задание…" : "Собрать сведения"}
            </button>
            {replacement && (
              <button
                type="button"
                className="button ghost"
                onClick={() => setReplacement(null)}
              >
                Закрыть
              </button>
            )}
          </form>
          <div className="section-heading">
            <h4>История пилота</h4>
            <button
              type="button"
              className="button ghost"
              disabled={disabled}
              onClick={() =>
                action("refresh", async () => {
                  const history = await load();
                  if (selected)
                    await open(
                      history.find((t) => t.taskId === selected.taskId) ||
                        selected,
                    );
                })
              }
            >
              Обновить историю
            </button>
          </div>
          {items.map((task) => (
            <div className="counterparty-history" key={task.taskId}>
              <button
                type="button"
                className="button ghost"
                disabled={disabled}
                onClick={() => action("open", () => open(task))}
              >
                ИНН {task.subject.inn} ·{" "}
                {task.statusReason?.code === "identity_conflict"
                  ? "Нужно уточнить компанию"
                  : labels[task.status] || task.status}{" "}
                · {date(task.createdAt)}
              </button>
              <TaskLaunchNumber task={task} compact/>
              {task.replacesTaskId && (
                <small>Повторный запуск после остановки</small>
              )}
            </div>
          ))}
          {!items.length && <p className="muted">Запусков пока нет.</p>}
          {selected && (
            <section>
              <TaskLaunchNumber task={selected}/>
              <nav className="section-nav">
                <button
                  type="button"
                  className="button ghost"
                  onClick={() => setMaterialView("dossier")}
                >
                  Материалы Агента 1
                </button>
                <button
                  type="button"
                  className="button ghost"
                  disabled={disabled}
                  onClick={() =>
                    action("technical", async () => {
                      setTechnical(
                        await api.request(
                          `${root}/tasks/${selected.taskId}/journal`,
                        ),
                      );
                      setMaterialView("technical");
                    })
                  }
                >
                  Технический журнал и расходы
                </button>
              </nav>
              <div className="counterparty-actions">
                {dossier &&
                  [
                    "json",
                    ...(dossier.artifactKind === "identity-resolution"
                      ? []
                      : ["md"]),
                  ].map((format) => (
                    <button
                      type="button"
                      key={format}
                      className="button ghost"
                      disabled={disabled}
                      onClick={() =>
                        action("download", async () => {
                          const value = await api.request(
                            `${root}/tasks/${selected.taskId}/dossier?format=${format}`,
                          );
                          download(
                            value,
                            format === "md"
                              ? "text/markdown"
                              : "application/json",
                            `counterparty-${selected.subject.inn}.${format}`,
                          );
                        })
                      }
                    >
                      Скачать {format.toUpperCase()}
                    </button>
                  ))}
                {["failed", "cancelled", "completed"].includes(
                  selected.status,
                ) && (
                  <button
                    type="button"
                    className="button ghost"
                    disabled={disabled}
                    onClick={() =>
                      action("recover", async () => {
                        await api.request(
                          `${root}/tasks/${selected.taskId}/recover`,
                          { method: "POST", body: {} },
                        );
                        await open(selected);
                        setNotice(
                          "Материалы восстановлены из журнала. Платные обращения не выполнялись.",
                        );
                      })
                    }
                  >
                    Восстановить материалы
                  </button>
                )}
                {["failed", "cancelled"].includes(selected.status) && (
                  <button
                    type="button"
                    className="button ghost"
                    disabled={disabled}
                    onClick={() => {
                      setReplacement(selected);
                      setInn(selected.subject.inn);
                      setPurpose(dossier?.input?.purpose || "general");
                      intent.current = null;
                    }}
                  >
                    {dossier?.code === "identity_conflict"
                      ? "Уточнить ИНН / повторить подготовку"
                      : "Новый запуск"}
                  </button>
                )}
              </div>
              {materialView === "dossier" ? (
                dossier ? (
                  <CounterpartyDossier dossier={dossier} journal={technical} />
                ) : (
                  <p>
                    Материалы пока недоступны. Состояние исполнения:{" "}
                    {labels[selected.status] || selected.status}.
                  </p>
                )
              ) : (
                technical && (
                  <div>
                    {technical.diagnostic && (
                      <p role="status">
                        {
                          preparationProblem(
                            { code: technical.diagnostic },
                            technical,
                          ).description
                        }
                      </p>
                    )}
                    <h4>Журнал исключений</h4>
                    {technical.exclusions?.map((x, i) => (
                      <p key={i}>
                        {x.statement} · {x.reason} · {x.evidence}
                      </p>
                    ))}
                    <h4>Сохранённые документы</h4>
                    {technical.snapshotIds?.map((id) => (
                      <details key={id}>
                        <summary>{id}</summary>
                        <button
                          type="button"
                          className="button ghost"
                          onClick={() =>
                            action("snapshot", async () => {
                              const doc = await api.request(
                                `${root}/tasks/${selected.taskId}/sources/${id}`,
                              );
                              download(doc, "application/json", `${id}.json`);
                            })
                          }
                        >
                          Скачать исходный материал и извлечённый текст
                        </button>
                      </details>
                    ))}
                    <h4>
                      Конфигурация задания, ход исследования и учёт расходов
                    </h4>
                    <pre className="code-block">
                      {JSON.stringify(technical, null, 2)}
                    </pre>
                  </div>
                )
              )}
            </section>
          )}
        </div>
      )}
    </article>
  );
}
